import React from 'react';
import { centsToDisplay } from '../../utils/price';

const CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

interface ItemDetailsProps {
  item: {
    name: string;
    description?: string | null;
    categories?: Array<{ id: number; name?: string; key?: string; label?: string }>;
    location?: string | null;
    area?: string | null;
    sub_location?: string | null;
    shared_with_count?: number;
    quantity?: number;
    added_by_name?: string | null;
    owner_name?: string | null;
    item_owner_name?: string | null;
    access_type?: string | null;
    user_id?: number;
    item_owner_id?: number;
    sale_price?: number | null;
    [key: string]: any;
  };
  showCategories?: boolean;
  showLocation?: boolean;
  showPrice?: boolean;
  showShareBadge?: boolean;
  showQuantityInName?: boolean;
  highlightZeroQuantity?: boolean; // Show orange color for zero quantity items
  showSharedBy?: boolean;
  currentUserId?: number;
  isSharedCollection?: boolean;
  onNameClick?: () => void;
  onQuantityClick?: () => void;
}

export const ItemDetails: React.FC<ItemDetailsProps> = ({
  item,
  showCategories,
  showLocation,
  showPrice,
  showShareBadge,
  showQuantityInName,
  highlightZeroQuantity,
  showSharedBy,
  currentUserId,
  isSharedCollection,
  onNameClick,
  onQuantityClick,
}) => {
  // Determine if item is shared and who shared it
  const sharedByName = item.added_by_name || item.owner_name || item.item_owner_name;
  
  // Item is shared if:
  // 1. We're in a shared collection, OR
  // 2. It has access_type 'shared_with_me', OR
  // 3. It has a different owner than current user
  const isSharedItem = isSharedCollection ||
                       item.access_type === 'shared_with_me' || 
                       (sharedByName && (currentUserId && item.user_id && item.user_id !== currentUserId)) ||
                       (sharedByName && (currentUserId && item.item_owner_id && item.item_owner_id !== currentUserId));
  
  // For guest views (no currentUserId), show all items with sharedByName
  // For user views in shared collections, always show badge
  // For user views not in shared collections, only show if has a name
  const shouldShowSharedBadge = !currentUserId 
    ? !!sharedByName 
    : (isSharedCollection || (isSharedItem && sharedByName));
  
  // Check if item is shared by current user (by ID or by checking if name appears to be current user)
  const isSharedByCurrentUser = currentUserId && 
                                 ((item.user_id && item.user_id === currentUserId) ||
                                  (item.item_owner_id && item.item_owner_id === currentUserId));
  
  return (
    <div className="item-details" style={{ flex: 1 }}>
      {/* Item name - clickable to open edit menu */}
      <div 
        className="item-name"
        onClick={(e) => {
          if (onNameClick) {
            e.stopPropagation();
            onNameClick();
          }
        }}
        style={{
          color: item.quantity === -1 
            ? 'var(--color-item-name-disabled)' // Dimmed for -1 (don't want to restock)
            : (showQuantityInName || highlightZeroQuantity) && item.quantity === 0 
              ? 'var(--color-warning)' // Orange for 0 (out of stock)
              : 'var(--color-item-name)', // Theme-aware item name color
          cursor: onNameClick ? 'pointer' : 'default',
        }}
      >
        {showQuantityInName && item.quantity !== undefined && (
          <span style={{ marginRight: '4px' }}>{item.quantity}</span>
        )}
        {item.name}
      </div>
      
      {/* Item ID + Condition for marketplace */}
      {showPrice && item.id && (
        <div className="item-description" style={{ fontSize: 'var(--font-size-xs)' }}>
          #{item.id}{item.condition && CONDITION_LABELS[item.condition] ? ` • ${CONDITION_LABELS[item.condition]}` : ''}
        </div>
      )}

      {/* Condition (non-marketplace) */}
      {!showPrice && item.condition && CONDITION_LABELS[item.condition] && (
        <div style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          fontStyle: 'italic',
        }}>
          {CONDITION_LABELS[item.condition]}
        </div>
      )}

      {/* Description */}
      {(showCategories || showPrice) && item.description && (
        <div className="item-description">
          {item.description}
        </div>
      )}

      {/* Marketplace price + quantity line */}
      {showPrice && (
        <div className="item-marketplace-info">
          {item.sale_price != null && item.sale_price > 0 && (
            <span className="item-marketplace-price">${centsToDisplay(item.sale_price)} each</span>
          )}
          {item.quantity != null && (
            <span
              className="item-marketplace-qty"
              onClick={onQuantityClick ? (e) => { e.stopPropagation(); onQuantityClick(); } : undefined}
              style={onQuantityClick ? { cursor: 'pointer' } : undefined}
            >
              Qty: <span className="item-marketplace-qty-value">{item.quantity}</span>
            </span>
          )}
        </div>
      )}

      {/* Categories (hidden in marketplace mode) */}
      {showCategories && !showPrice && item.categories && item.categories.length > 0 && (
        <div className="item-categories">
          {item.categories.map((cat) => (
            <span
              key={cat.id}
              className="category-badge"
            >
              {cat.name || cat.label || cat.key || 'Category'}
            </span>
          ))}
        </div>
      )}
      
      {/* Location (hidden in marketplace mode) */}
      {showLocation && !showPrice && (item.area || item.location || item.sub_location) && (
        <div className="item-location">
          📍 {[item.area, item.location, item.sub_location].filter(Boolean).join('•')}
        </div>
      )}
      
      {/* Share badge */}
      {showShareBadge && item.shared_with_count !== undefined && item.shared_with_count > 0 && (
        <div className="item-share-badge">
          👥 Shared with {item.shared_with_count} {item.shared_with_count === 1 ? 'person' : 'people'}
        </div>
      )}
      
      {/* Shared badge - gray to indicate non-clickable status indicator */}
      {showSharedBy && shouldShowSharedBadge && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '4px',
          padding: '2px 8px',
          background: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-secondary)',
          borderRadius: '4px',
          fontSize: 'var(--font-size-xs)',
          fontWeight: '500'
        }}>
          {/* Link/chain icon - shared/connected */}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
          {
            // In shared collections, if item is by current user OR no name, show "Shared"
            // Otherwise show "Shared by [name]"
            isSharedByCurrentUser || !sharedByName || 
            (isSharedCollection && item.access_type !== 'shared_with_me')
              ? 'Shared' 
              : `Shared by ${sharedByName}`
          }
        </div>
      )}
    </div>
  );
};
